import { lineString, point, polygon } from "@turf/helpers";
import type { Feature, LineString, Point, Polygon, Position } from "geojson";

import {
  KeyShortcut,
  SpecialKeys,
} from "@/features/app/components/key_shortcut";
import { PopupMenu } from "@/features/app/components/modals/PopupMenu";
import appendCoordinate from "@/features/geojson_editor/libs/geojson/appendCoordinate";
import { isGeometryValid } from "@/features/geojson_editor/libs/geojson/geometryUtils";
import { createEditorMetaFromFeature } from "@/features/geojson_editor/meta";
import { BaseMode } from "@/features/geojson_editor/modes/BaseMode";
import type { EventData } from "@/features/map/hooks/events/types";
import { calculateSnappedPosition } from "../libs/geojson/Snapping";

/**
 * DrawMode - Interactive polygon drawing mode with orthogonal snapping
 *
 * Features:
 * - Click to add vertices
 * - Hold Ctrl to enable orthogonal snapping (0°/90°/180°/270° angles)
 * - Hover over first vertex to close polygon
 * - Right-click for context menu
 * - ESC to cancel, Enter to finish
 */
export class DrawMode extends BaseMode {
  // Drawing state
  private isDrawing: boolean = false;
  private newFeature: Feature<Polygon> | null = null;

  // Cursor and interaction state
  private cursorPosition: { lat: number; lng: number } | null = null;
  private closingVertexHovered: boolean = false;

  // Snapping mode
  private isOrthogonalSnappingEnabled: boolean = false;

  // =============================================================================
  // Lifecycle Methods
  // =============================================================================

  init() {
    this.changeCursor("crosshair");
  }

  destroy() {
    this.changeCursor(null);
  }

  setFeatures(_fs: Record<string, Feature>): void {
    // DrawMode doesn't need to load existing features
    return;
  }

  // =============================================================================
  // Meta Features Generation
  // =============================================================================

  generateMetaFeatures(): Feature[] {
    if (!this.shouldRecalculateMetaFeatures()) {
      return this._cachedMetaFeatures;
    }

    if (!this.newFeature) {
      this._cachedMetaFeatures = [];
      return [];
    }

    // Start with the current feature
    let featureForMeta:
      | Feature<Polygon>
      | Feature<LineString>
      | Feature<Point> = this.newFeature;

    // Add preview of cursor position while drawing
    if (this.isDrawing && this.cursorPosition) {
      featureForMeta = appendCoordinate(
        this.newFeature,
        [this.cursorPosition.lng, this.cursorPosition.lat],
        { skipValidation: true },
      ) as Feature<Polygon>;
    }

    // Handle incomplete geometries
    // TODO: Remove hardcoded assumption of single ring geometries
    const coordCount = featureForMeta.geometry.coordinates[0].length;

    if (coordCount === 2) {
      // Only one vertex: show as a point
      featureForMeta = point(
        featureForMeta.geometry.coordinates[0][0] as Position,
        this.newFeature.properties,
        { id: this.newFeature.id },
      );
    } else if (coordCount === 3) {
      // Two vertices: show as a line
      featureForMeta = lineString(
        [
          featureForMeta.geometry.coordinates[0][0] as Position,
          featureForMeta.geometry.coordinates[0][1] as Position,
        ],
        this.newFeature.properties,
        { id: this.newFeature.id },
      );
    }

    // Generate visual elements (vertices, lines, fill)
    const metaFeatures = createEditorMetaFromFeature(featureForMeta, {
      includeCenterPoint: false,
      includeFill: true,
      includeLine: true,
      includeVertex: true,
    });

    this._cachedMetaFeatures = metaFeatures;
    return metaFeatures;
  }

  // =============================================================================
  // Drawing State Management
  // =============================================================================

  private startDrawing(coordinate: Coordinate): void {
    this.isDrawing = true;
    this.newFeature = polygon([], { id: this.generateUniqueId() });
    this.newFeature = appendCoordinate(this.newFeature, coordinate, {
      skipValidation: true,
    }) as Feature<Polygon>;
  }

  private cancelDrawing(): void {
    if (!this.newFeature || !this.isDrawing) return;
    this._clearState();
  }

  private finishDrawing(): void {
    if (!this.newFeature) return;

    this.onChange(
      {
        added: [this.newFeature],
      },
      true,
    );
    this._clearState();
  }

  private _clearState(): void {
    this.isDrawing = false;
    this.newFeature = null;
    this.cursorPosition = null;
    this.forceReRender();
  }

  // =============================================================================
  // Snapping Control
  // =============================================================================

  /**
   * Toggles orthogonal snapping mode (activated by Ctrl key)
   */
  private toggleOrthogonalSnapping(): void {
    if (!this.newFeature) return;
    this.isOrthogonalSnappingEnabled = !this.isOrthogonalSnappingEnabled;
  }

  // =============================================================================
  // Coordinate Utilities
  // =============================================================================

  /**
   * Extracts coordinates from geometry, handling both LineString and Polygon types
   */
  private extractCoordinates(geomCoords: unknown): Coordinate[] {
    // Polygon: Coordinate[][]
    if (
      Array.isArray(geomCoords) &&
      geomCoords.length > 0 &&
      Array.isArray(geomCoords[0]) &&
      Array.isArray(geomCoords[0][0])
    ) {
      return geomCoords[0] as Coordinate[];
    }

    // LineString: Coordinate[]
    if (Array.isArray(geomCoords) && geomCoords.length > 0) {
      return geomCoords as Coordinate[];
    }

    return [];
  }

  /**
   * Applies orthogonal snapping to cursor position if enabled
   */
  private getPosition(
    rawLat: number,
    rawLng: number,
  ): { lat: number; lng: number } {
    if (!this.isOrthogonalSnappingEnabled || !this.newFeature) {
      return { lat: rawLat, lng: rawLng };
    }

    const coordinates = this.extractCoordinates(
      this.newFeature.geometry.coordinates,
    );

    return calculateSnappedPosition(rawLng, rawLat, coordinates);
  }

  // =============================================================================
  // Vertex Detection
  // =============================================================================

  /**
   * Checks if a feature is the closing vertex (first vertex of polygon)
   */
  private _isClosingVertex(feature: Feature): boolean {
    return feature.properties?.metaCoordinateIdx === "0.0";
  }

  // =============================================================================
  // Event Handlers
  // =============================================================================

  eventHandlers: BaseMode["eventHandlers"] = {
    /**
     * ESC - Cancel drawing
     */
    onCancel: () => {
      this.cancelDrawing();
      return true;
    },

    /**
     * Ctrl - Toggle orthogonal snapping
     */
    onControl: () => {
      this.toggleOrthogonalSnapping();
    },

    /**
     * Delete - Cancel drawing
     */
    onDelete: () => {
      this.cancelDrawing();
    },

    /**
     * Enter - Finish drawing
     */
    onEnter: () => {
      this.finishDrawing();
    },

    /**
     * Click - Add vertex or start drawing
     */
    onMapClick: (e: EventData): boolean => {
      // Start new polygon
      if (!this.isDrawing) {
        this.startDrawing([e.coordinates.lng, e.coordinates.lat]);
        return true;
      }

      // Add vertex to existing polygon
      const position = this.getPosition(e.coordinates.lat, e.coordinates.lng);

      const coordinate: Coordinate = [position.lng, position.lat];

      this.newFeature = appendCoordinate(
        this.newFeature as Feature<Polygon>,
        coordinate,
        { skipValidation: true },
      ) as Feature<Polygon>;

      this.forceReRender();
      return true;
    },

    /**
     * Right Click - Show context menu
     */
    onMapRightClick: (e) => {
      if (!this.isDrawing || !isGeometryValid(this.newFeature)) {
        return;
      }

      this._openPopupMenu(
        <>
          <PopupMenu.Item
            description={<KeyShortcut hotkeys={[SpecialKeys.ENTER]} />}
            text="Finish polygon"
            onClick={() => this.finishDrawing()}
          />
          <PopupMenu.Item
            description={<KeyShortcut hotkeys={[SpecialKeys.ESCAPE]} />}
            text="Cancel polygon"
            onClick={() => this.cancelDrawing()}
          />
        </>,
        { x: e.point.x, y: e.point.y },
      );
    },

    /**
     * Mouse Move - Update cursor preview
     */
    onMouseMove: (e): void => {
      if (!this.isDrawing || this.closingVertexHovered) return;

      this.cursorPosition = this.getPosition(e.lngLat.lat, e.lngLat.lng);
      this.forceReRender();
    },

    /**
     * Vertex Click - Close polygon if clicking on first vertex
     */
    onVertexClick: (e: EventData): boolean => {
      if (e.features.find(this._isClosingVertex)) {
        this.finishDrawing();
        return true;
      }
      return false;
    },

    /**
     * Vertex Hover - Snap to first vertex when hovering to close polygon
     */
    onVertexHover: (e) => {
      const closingVertex = e.features.find(this._isClosingVertex) as
        | Feature<Point>
        | undefined;

      if (closingVertex) {
        this.closingVertexHovered = true;
        this.cursorPosition = {
          lat: closingVertex.geometry.coordinates[1],
          lng: closingVertex.geometry.coordinates[0],
        };
        this.forceReRender();
        return true;
      }
    },

    /**
     * Vertex Hover End - Resume normal cursor tracking
     */
    onVertexHoverEnd: () => {
      this.closingVertexHovered = false;
      this.forceReRender();
    },
  };
}
